import type { User } from '@repo-level/core-front';
import {
  AuthContext,
  CommonContext,
  dateFormat,
  playerFields,
  removeSpaces,
  UpdatePlayerContext,
  updatePlayer,
  useApi,
} from '@repo-level/core-front';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';
import { Button } from 'components/buttons';
import { InputDate } from 'components/form/Date';
import { FormSection } from 'components/form/groups/FormSection';
import { Input } from 'components/form/Input';
import {
  InputPhoneNumber,
  ScAddressInput,
  ScInputNameSurname,
  validatePhone,
} from 'components/form/InputPhoneNumber';
import { Select } from 'components/form/Select';
import type { ModalDefaultProps } from 'components/Modal';
import { ModalTemplate } from 'components/ModalTemplate';
import type { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { Spinner } from 'components/Spinner';
import { AddonsContext } from 'contexts/addons';
import { ModalsContext, ModalType } from 'contexts/modal';
import { StagContext } from 'contexts/stag';
import { abTestingFunction } from 'helpers/abTesting';
import { notEmpty } from 'helpers/common';
import { countriesWithPhoneNumber } from 'helpers/countriesWithPhoneNumber';
import { LocalStorageKeys } from 'helpers/localStorage';
import { maskPhone } from 'helpers/masks';
import { showTooltip } from 'helpers/tooltip';
import { translationFactory } from 'helpers/trans';
import {
  adultValidator,
  composeValidators,
  dateRegexValidator,
  required,
} from 'helpers/validators';
import { useCountryOptions } from 'hooks/common';
import { useFranceLocale } from 'hooks/franceLocale';
import type { FormApi } from 'informed';
import { Form } from 'informed';
import { parsePhoneNumber } from 'libphonenumber-js';
import { AB_TESTS } from 'modules/abTesting/constants';
import { useABTestingV2 } from 'modules/abTesting/hooks/useABTesting';
import { useMedia } from 'modules/common/hooks/useMedia';
import { TooltipTypes } from 'modules/globalTooltip';
import {
  sendAdditionalUserDataAppearanceEvent,
  sendAdditionalUserDataFormCloseEvent,
  sendAdditionalUserDataResultEvent,
} from 'modules/googleAnalytics/events/additionalDataEvents';
import { canAssignMission } from 'modules/missions/helpers/mission';
import { useAssignMission } from 'modules/missions/hooks/useAssignMission';
import { MissionId } from 'modules/missions/types';
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { getNdbEmailModal } from '../NDB/helpers';
import {
  ABTEST_ADDITIONAL_DATA_AFTER_REG,
  ABTEST_ADDITIONAL_DATA_AFTER_REG_A,
  ABTEST_ADDITIONAL_DATA_AFTER_REG_B,
} from '../Registration/helpers';
import { ScAdditionalDataFormContainer } from './styled';
import type { TAdditionalDataOrigin } from './types';
import { useAdditionalDataOrigin } from './useAdditionalDataOrigin';

const validateDate = composeValidators(
  required,
  dateRegexValidator,
  adultValidator
);

export interface FullInformationFormApi {
  firstName: string;
  lastName: string;
  dateOfBirth: {
    day: string;
    month: string;
    year: string;
  };
  mobilePhone: {
    code: string;
    number: string;
  };
  country: string;
  city: string;
  address: string;
  postalCode: string;
  state?: string;
}

const FullInformation = forwardRef<
  HTMLDivElement,
  ModalDefaultProps & { openedFrom?: TAdditionalDataOrigin }
>(({ onClose, openedFrom }, ref) => {
  const { t } = useTranslation();
  const { requestAssignMission } = useAssignMission();

  const { isFR, isFRWithStag, isFRWithoutStag } = useFranceLocale();
  const { isB: isShowQuests } = useABTestingV2({
    experiment: AB_TESTS.QUESTS,
  });

  const { currentIp } = useContext(CommonContext);
  const { fetchUser, user, pendingUser, pendingWallets } =
    useContext(AuthContext);

  const isAssignMission = useMemo(
    () => canAssignMission(user, isShowQuests, MissionId.WN_2),
    [isShowQuests, user]
  );

  const { cmsABTests } = useContext(AddonsContext);
  const { isMobileS } = useMedia();
  const { isStagNDB } = useContext(StagContext);
  const hasConfirmEmailShown = localStorage.getItem(
    LocalStorageKeys.ConfirmEmailNDB
  );

  const isAdditionalDataAfterReg =
    abTestingFunction(
      ABTEST_ADDITIONAL_DATA_AFTER_REG,
      {
        turnOffTest: true,
        defaultVersion: ABTEST_ADDITIONAL_DATA_AFTER_REG_A,
        versions: [
          {
            name: ABTEST_ADDITIONAL_DATA_AFTER_REG_A,
            probability: 50,
          },
          {
            name: ABTEST_ADDITIONAL_DATA_AFTER_REG_B,
            probability: 50,
          },
        ],
      },
      user,
      currentIp,
      cmsABTests
    ) === ABTEST_ADDITIONAL_DATA_AFTER_REG_B;

  const { origin, forgetOrigin } = useAdditionalDataOrigin(openedFrom);

  const formApiRef = useRef<FormApi<FullInformationFormApi>>();

  const [
    requestUpdatePlayer,
    { pending: pendingUpdatePlayer, error: updatePlayerErrors },
  ] = useApi(updatePlayer);

  const [
    requestStatePlayer,
    { data: dataStatePlayer, pending: pendingStatePlayer },
  ] = useApi(playerFields);

  useEffect(() => {
    if (!dataStatePlayer && currentIp?.countryCode) {
      requestStatePlayer(user?.country || currentIp.countryCode);
    }
  }, [currentIp?.countryCode, dataStatePlayer, requestStatePlayer]);

  const templateSettings: ModalTemplateSettings = useMemo(
    () =>
      isMobileS
        ? {
            type: 'mobile',
            mode: {
              type: 'default',
            },
          }
        : {
            type: 'desktop',
            size: 'large',
            mode: {
              type: 'default',
            },
          },
    [isMobileS]
  );

  const errors = useMemo(() => {
    if (!updatePlayerErrors?.errors) return null;
    return Object.keys(updatePlayerErrors.errors).map((key) => ({
      field: key,
      message: Object.values(updatePlayerErrors.errors[key]),
    }));
  }, [updatePlayerErrors?.errors]);

  useEffect(() => {
    if (!errors?.length) return;
    errors.forEach(({ field, message }) => {
      if (field === 'mobilePhone') {
        formApiRef.current?.setError('mobilePhone.number', message);
      } else {
        formApiRef.current?.setError(field, message);
      }
    });
  }, [errors]);

  const { handleModal } = useContext(ModalsContext);

  const defaultCountry = useMemo(() => currentIp?.countryCode, [currentIp]);

  const countryOptions = useCountryOptions();

  const statesOptions = useMemo(() => {
    if (!dataStatePlayer) return null;

    const { fields } = dataStatePlayer;

    const stateField = fields.find((item) => item.field === 'state');

    return stateField?.inclusion.in.map((state: string) => ({
      name: state,
      value: state,
    }));
  }, [dataStatePlayer]);

  const showErrorTooltip = () => {
    showTooltip({
      type: TooltipTypes.ERROR,
      translate: true,
      title: translationFactory(
        'common:auth.fullInformation.tooltips.failure.title'
      ),
      text: translationFactory(
        'common:auth.fullInformation.tooltips.failure.text'
      ),
    });
  };

  const handleSubmit = async ({ ...values }: FullInformationFormApi) => {
    const mobilePhone = (() => {
      if (values?.mobilePhone?.code == null) return;

      const mobileCode = countriesWithPhoneNumber.find(
        ({ iso2 }) => iso2 === values.mobilePhone.code
      );

      if (mobileCode == null) return;

      return removeSpaces(
        `+${mobileCode.dialCode}${values.mobilePhone.number}`
      );
    })();
    const mobilePhoneWithoutDialCode = removeSpaces(values.mobilePhone.number);

    const { error } = await requestUpdatePlayer({
      context: UpdatePlayerContext.Edition,
      player: {
        ...values,
        dateOfBirth: dateFormat(
          `${values.dateOfBirth.day}/${values.dateOfBirth.month}/${values.dateOfBirth.year}`
        ),
        ...(mobilePhone && !isFR ? { mobilePhone } : {}),
        ...(mobilePhoneWithoutDialCode && isFR
          ? { mobilePhoneWithoutDialCode }
          : {}),
      } as Partial<User>,
    }).request;

    if (error != null) {
      sendAdditionalUserDataResultEvent({
        status: 'failed',
        error_data: error,
      });
      showErrorTooltip();
      return;
    }

    sendAdditionalUserDataResultEvent({ status: 'success' });
    showTooltip({
      type: TooltipTypes.SUCCESS,
      translate: true,
      title: translationFactory(
        'common:auth.fullInformation.tooltips.success.title'
      ),
      text: translationFactory(
        'common:auth.fullInformation.tooltips.success.text'
      ),
    });

    const refreshedUser = await fetchUser(
      REASON_FOR_UPDATING_USER.REGISTRATION
    );

    const isEmailConfirmed = !!(refreshedUser
      ? refreshedUser.confirmedAt
      : user?.confirmedAt);

    if (isAssignMission) {
      await requestAssignMission(MissionId.WN_2).request;
    }

    onClose && onClose();
    forgetOrigin();

    // The provider path is always the last step of a registration; the email
    // one reaches this modal for NDB stags, or via the AB test when enabled.
    const isAfterRegistration =
      origin === 'google-registration' ||
      ((isStagNDB || isAdditionalDataAfterReg) && origin === 'registration');

    if (isAfterRegistration) {
      if (isStagNDB) {
        handleModal(getNdbEmailModal(isEmailConfirmed))();
        return;
      }

      handleModal(ModalType.Welcome)();
      return;
    }

    handleModal(ModalType.Deposit)();
  };

  const completed = isFRWithoutStag
    ? !!user?.firstName && !!user?.lastName && !!user?.dateOfBirth
    : !!user?.firstName &&
      !!user?.lastName &&
      !!user?.dateOfBirth &&
      !!user?.mobilePhone &&
      !!user?.country &&
      !!user?.state &&
      !!user?.city &&
      !!user?.address &&
      !!user?.postalCode;

  const submitButtonParams = useMemo(
    () => (isMobileS ? { wide: true } : { _width: 232 }),
    [isMobileS]
  );

  const handleClickSubmit = () => {
    if (!formApiRef.current) return;
    const { errors } = formApiRef.current.getState();
    if (Object.keys(errors).length) {
      showErrorTooltip();
    }
  };

  const handleClose = useCallback(() => {
    sendAdditionalUserDataFormCloseEvent();
    onClose && onClose();
    forgetOrigin();

    if (!isStagNDB || hasConfirmEmailShown) return;

    handleModal(getNdbEmailModal(!!user?.confirmedAt))();
  }, [
    forgetOrigin,
    handleModal,
    onClose,
    isStagNDB,
    user?.confirmedAt,
    hasConfirmEmailShown,
  ]);

  useEffect(() => {
    sendAdditionalUserDataAppearanceEvent();
  }, []);

  return pendingUser || pendingWallets ? (
    <Spinner absoluteCenter size={64} />
  ) : (
    <ModalTemplate onClose={handleClose} ref={ref} settings={templateSettings}>
      <Form<FullInformationFormApi>
        getApi={(api) => {
          formApiRef.current = api;
        }}
        onSubmit={handleSubmit}
      >
        <ScAdditionalDataFormContainer>
          <div className="header">
            <div className="title">{t('common:messageFromElf')}</div>
            <div className="slogan">{t('common:messageFromElfDescr')}</div>
          </div>

          <div className="fields-container">
            <FormSection title={t('glossary:name')}>
              <ScInputNameSurname>
                <Input
                  animatedPlaceholder={t('glossary:firstName')}
                  disabled={notEmpty(user?.firstName)}
                  field="firstName"
                  initialValue={user?.firstName || undefined}
                  validate={required}
                  validateOnBlur
                />
                <Input
                  animatedPlaceholder={t('glossary:lastName')}
                  disabled={notEmpty(user?.lastName)}
                  field="lastName"
                  initialValue={user?.lastName || undefined}
                  validate={required}
                  validateOnBlur
                />
              </ScInputNameSurname>
            </FormSection>
            <FormSection title={t('glossary:dateOfBirth')}>
              <InputDate
                disabled={notEmpty(user?.dateOfBirth)}
                field="dateOfBirth"
                initialValue={user?.dateOfBirth || undefined}
                validate={validateDate}
              />
            </FormSection>
            {isFRWithStag ? (
              <>
                <FormSection title={t('glossary:phoneNumber')}>
                  <Input
                    animatedPlaceholder="Number"
                    disabled={notEmpty(user?.mobilePhone)}
                    field="mobilePhone.number"
                    initialValue={
                      user?.mobilePhone
                        ? (parsePhoneNumber(user?.mobilePhone)
                            .nationalNumber as string)
                        : undefined
                    }
                    mask={(v) => maskPhone(v?.toString(), 'FR')}
                    validate={(v) => validatePhone(v, 'FR')}
                    validateOnBlur
                  />
                </FormSection>
                <FormSection title={t('glossary:address')}>
                  <ScAddressInput>
                    <Input
                      animatedPlaceholder={t('glossary:city')}
                      disabled={notEmpty(user?.city)}
                      field="city"
                      initialValue={user?.city || undefined}
                      validate={required}
                      validateOnBlur
                    />
                    <Input
                      animatedPlaceholder={t('glossary:address')}
                      disabled={notEmpty(user?.address)}
                      field="address"
                      initialValue={user?.address || undefined}
                      validate={required}
                      validateOnBlur
                    />
                    <Input
                      animatedPlaceholder={t('glossary:postal')}
                      disabled={notEmpty(user?.postalCode)}
                      field="postalCode"
                      initialValue={user?.postalCode || undefined}
                      validate={required}
                      validateOnBlur
                    />
                  </ScAddressInput>
                </FormSection>
              </>
            ) : (
              <>
                <FormSection title={t('glossary:phoneNumber')}>
                  <InputPhoneNumber
                    disabled={notEmpty(user?.mobilePhone)}
                    field="mobilePhone"
                    initialCodeValue={defaultCountry}
                    initialValue={user?.mobilePhone || undefined}
                    validateOnBlur
                    withValidate
                  />
                </FormSection>
                <FormSection
                  noMargin={notEmpty(user?.country)}
                  title={t('glossary:address')}
                >
                  {notEmpty(user?.country) && (
                    <p className="country-info-msg">
                      <Trans
                        components={{
                          l: <span id="intercomcustomLauncher" />,
                        }}
                        i18nKey="addressInfoMsg"
                        ns="common"
                      />
                    </p>
                  )}
                  <ScAddressInput>
                    <Select
                      animatedPlaceholder={t('glossary:country')}
                      disabled={notEmpty(user?.country)}
                      field="country"
                      initialValue={
                        user?.country || defaultCountry || undefined
                      }
                      minOptionHeight={48}
                      onSelect={(country: any) => {
                        requestStatePlayer(country);
                      }}
                      options={countryOptions}
                      search
                      selectedOptionWithoutIcon
                      validate={required}
                      withBorderMode
                    />
                    {pendingStatePlayer ? (
                      <Spinner center size={48} />
                    ) : (
                      statesOptions && (
                        <Select
                          animatedPlaceholder={t('glossary:state')}
                          disabled={notEmpty(user?.state)}
                          field="state"
                          maxHeight={150}
                          minOptionHeight={48}
                          options={statesOptions}
                          search
                          selectedOptionWithoutIcon
                          validate={required}
                          validateOnBlur
                          withBorderMode
                        />
                      )
                    )}
                    <Input
                      animatedPlaceholder={t('glossary:city')}
                      disabled={notEmpty(user?.city)}
                      field="city"
                      initialValue={user?.city || undefined}
                      validate={required}
                      validateOnBlur
                    />
                    <Input
                      animatedPlaceholder={t('glossary:address')}
                      disabled={notEmpty(user?.address)}
                      field="address"
                      initialValue={user?.address || undefined}
                      validate={required}
                      validateOnBlur
                    />
                    <Input
                      animatedPlaceholder={t('glossary:postal')}
                      disabled={notEmpty(user?.postalCode)}
                      field="postalCode"
                      initialValue={user?.postalCode || undefined}
                      validate={required}
                      validateOnBlur
                    />
                  </ScAddressInput>
                </FormSection>
              </>
            )}
          </div>
          <div className="submit-btn-container">
            <Button
              disabled={completed}
              green
              onClick={handleClickSubmit}
              pending={pendingUpdatePlayer}
              type="submit"
              xLarge
              {...submitButtonParams}
            >
              {t('glossary:confirm')}
            </Button>
          </div>
        </ScAdditionalDataFormContainer>
      </Form>
    </ModalTemplate>
  );
});

export default FullInformation;
